import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { submitContact } from '../api';

const wrap = { maxWidth: 860, margin: '0 auto', padding: '40px 20px 60px', color: 'var(--text)' };
const h1 = { fontSize: 32, fontWeight: 800, marginBottom: 10 };
const h2 = { fontSize: 22, fontWeight: 800, margin: '40px 0 14px' };
const p = { fontSize: 15, lineHeight: 1.7, color: 'var(--muted)', margin: '0 0 12px' };
const label = { display: 'block', fontSize: 13, color: 'var(--muted)', margin: '14px 0 6px', fontWeight: 600 };
const inputBase = {
  width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: 14,
  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)',
};
const card = {
  background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: '18px 18px',
};

const CAPABILITIES = [
  { icon: '⚙️', title: 'Run backtests', body: 'Submit a strategy and date range, get the full run back — programmatically, at whatever cadence your research needs.' },
  { icon: '📊', title: 'Full trade & performance data', body: 'Pull the complete trade log, equity curve, and performance stats as clean JSON — no scraping the UI.' },
  { icon: '📈', title: 'Market data', body: 'Fetch OHLCV price data across stocks, forex, and crypto — including NSE (Indian equities) and global markets.' },
  { icon: '🧩', title: 'Strategy export', body: 'Export any strategy to Python backtrader or TradingView Pine Script to run and extend locally.' },
];

const PRICING = [
  { label: 'Upfront cost', value: 'None', note: 'No subscription required to start' },
  { label: 'Free every month', value: '100 backtests', note: 'Enough to build and validate' },
  { label: 'Standard backtest', value: '$0.04', note: 'per run' },
  { label: 'Heavy run (walk-forward / optimization)', value: '$0.25', note: 'per run' },
  { label: 'Market data & strategy exports', value: 'Free', note: 'within fair use' },
  { label: 'Credits', value: '$25 / $50 packs', note: 'optional auto-top-up' },
  { label: 'Pro API (optional)', value: '$25 / mo', note: 'higher rate limits, priority execution, 1,000 bundled backtests' },
];

export default function ApiPage() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.username || '',
    email: user?.email || '',
    subject: 'API access inquiry',
    message: '',
    website: '', // honeypot
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setErrorMsg('');
    const next = {};
    if (!form.email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) {
      next.email = 'Please enter a valid email.';
    }
    if (!form.message.trim()) next.message = 'Tell us a bit about your use case.';
    if (Object.keys(next).length) { setErrors(next); return; }
    setStatus('sending');
    try {
      await submitContact(form);
      setStatus('sent');
    } catch (err) {
      setStatus('');
      const data = err?.response?.data;
      if (data?.errors) setErrors(data.errors);
      setErrorMsg(data?.detail || 'Something went wrong. Please try again.');
    }
  };

  return (
    <div style={wrap}>
      <Helmet>
        <title>Custom Trading APIs — Algovex</title>
        <meta name="description" content="Algovex builds custom trading APIs on our backtesting engine — run backtests, pull full trade and performance data, fetch market data (incl. NSE), and export strategies. Usage-based pricing, no upfront cost. Tell us your use case." />
      </Helmet>

      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: 'var(--teal)', marginBottom: 12 }}>FOR DEVELOPERS &amp; QUANTS</div>
      <h1 style={h1}>Custom trading APIs, built to your spec</h1>
      <p style={{ ...p, fontSize: 16 }}>
        We don't ship a one-size-fits-all public API. Instead we build a <strong style={{ color: 'var(--text)' }}>customized
        API tailored to your workflow</strong> — the exact endpoints you need, on the same engine that powers Algovex.
        Tell us your use case and we'll build it. Pricing is usage-based, so you only pay for what you actually run.
      </p>

      <h2 style={h2}>What you can build on</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
        {CAPABILITIES.map((c) => (
          <div key={c.title} style={card}>
            <div style={{ fontSize: 22, marginBottom: 6 }}>{c.icon}</div>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>{c.title}</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>{c.body}</div>
          </div>
        ))}
      </div>

      <h2 style={h2}>Pricing</h2>
      <p style={p}>
        No upfront subscription and no flat monthly fee — usage-based, so it scales with what you run.
        We build your endpoints, an API key, and rate limits once you share your use case.
      </p>
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        {PRICING.map((row, i) => (
          <div
            key={row.label}
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 16,
              padding: '12px 16px',
              borderTop: i === 0 ? 'none' : '1px solid var(--border)',
            }}
          >
            <div style={{ fontSize: 14 }}>
              <div style={{ fontWeight: 600 }}>{row.label}</div>
              {row.note && <div style={{ fontSize: 12, color: 'var(--muted)' }}>{row.note}</div>}
            </div>
            <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--teal)', whiteSpace: 'nowrap' }}>{row.value}</div>
          </div>
        ))}
      </div>
      <p style={{ ...p, fontSize: 12, marginTop: 10 }}>
        Market data is sourced from third-party providers; coverage and redistribution are subject to their terms.
        Final rate limits and any custom endpoints are scoped to your use case.
      </p>

      <h2 style={h2}>Tell us about your use case</h2>
      <p style={p}>
        Share what you're building — the operations you need, markets/symbols (e.g. NSE), rough volume, and whether
        it's research or live automation. We'll come back with your endpoints, an API key, and pricing for your volume.
      </p>

      {status === 'sent' ? (
        <div style={{ ...card, background: 'rgba(47,214,195,0.1)', borderColor: 'var(--teal)', marginTop: 8 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>✅ Got it</div>
          <div style={{ fontSize: 14, color: 'var(--muted)' }}>
            Thanks — we'll reply to <strong>{form.email}</strong> to scope your API.
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={label}>Name</label>
              <input style={inputBase} value={form.name} onChange={set('name')} placeholder="Your name" />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={label}>Email <span style={{ color: 'var(--red)' }}>*</span></label>
              <input
                style={errors.email ? { ...inputBase, borderColor: 'var(--red)' } : inputBase}
                value={form.email} onChange={set('email')} placeholder="you@example.com" type="email"
              />
              {errors.email && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 4 }}>{errors.email}</div>}
            </div>
          </div>

          <label style={label}>Your use case <span style={{ color: 'var(--red)' }}>*</span></label>
          <textarea
            style={errors.message
              ? { ...inputBase, borderColor: 'var(--red)', minHeight: 150, resize: 'vertical' }
              : { ...inputBase, minHeight: 150, resize: 'vertical' }}
            value={form.message} onChange={set('message')}
            placeholder="What operations do you need (backtests, trade data, market data, exports)? Which markets/symbols? Rough volume? Research or live automation?"
            maxLength={5000}
          />
          {errors.message && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 4 }}>{errors.message}</div>}

          {/* Honeypot */}
          <input
            type="text" tabIndex={-1} autoComplete="off"
            value={form.website} onChange={set('website')}
            style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
            aria-hidden="true"
          />

          {errorMsg && (
            <div style={{
              background: 'rgba(248,81,73,0.12)', border: '1px solid rgba(248,81,73,0.4)',
              borderRadius: 6, padding: '10px 14px', margin: '16px 0 0', color: '#f85149', fontSize: 13,
            }}>{errorMsg}</div>
          )}

          <button
            type="submit"
            disabled={status === 'sending'}
            style={{
              marginTop: 20, background: 'var(--teal)', color: 'var(--bg)', border: 'none',
              padding: '11px 22px', borderRadius: 8, fontSize: 14, fontWeight: 700,
              cursor: status === 'sending' ? 'default' : 'pointer', opacity: status === 'sending' ? 0.7 : 1,
            }}
          >
            {status === 'sending' ? 'Sending…' : 'Request API access'}
          </button>
        </form>
      )}
    </div>
  );
}
